import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Loop-proof guard for public entry points (`/`, `/welcome`, `/start`, etc.).
 *
 * If an authenticated **admin** lands on a public page without an active
 * subscription, redirect them to `/subscribe` so they can't sit on marketing
 * screens or slip into protected flows.
 *
 * Safety:
 *  • Redirects at most once per mount (`hasRedirectedRef`)
 *  • Each user_id is checked once — token refreshes / USER_UPDATED don't re-query
 *  • Concurrent calls are prevented (`inFlightRef`)
 *  • Active / trialing / past_due users are NEVER redirected
 *  • Only acts if the user is still on a path in `gateForPaths`
 *  • Sign-out resets the cache so a different user is re-evaluated cleanly
 */
const ACTIVE_STATES = new Set(["active", "trialing", "trial", "past_due"]);

export function useExpiredSubscriptionRedirect(
  gateForPaths: readonly string[] = ["/", "/welcome", "/start", "/free-trial"]
) {
  const navigate = useNavigate();
  const hasRedirectedRef = useRef(false);
  const checkedUserIdsRef = useRef<Set<string>>(new Set());
  const inFlightRef = useRef(false);

  const check = useCallback(async (userId: string) => {
    if (hasRedirectedRef.current) return;
    if (checkedUserIdsRef.current.has(userId)) return;
    if (inFlightRef.current) return;
    if (!gateForPaths.includes(window.location.pathname)) return;

    inFlightRef.current = true;
    try {
      const [{ data: roleRow }, { data: sub }] = await Promise.all([
        supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", userId)
          .maybeSingle(),
        supabase
          .from("user_subscriptions")
          .select("subscription_status")
          .eq("user_id", userId)
          .maybeSingle(),
      ]);

      checkedUserIdsRef.current.add(userId);

      // Only gate admins — drivers don't manage billing.
      if (roleRow?.role !== "admin") return;

      const status = sub?.subscription_status;
      if (status && ACTIVE_STATES.has(status)) return;

      if (hasRedirectedRef.current) return;
      if (!gateForPaths.includes(window.location.pathname)) return;

      hasRedirectedRef.current = true;
      navigate("/subscribe", { replace: true });
    } catch (e) {
      console.warn("[ExpiredSubGuard] Check failed:", e);
    } finally {
      inFlightRef.current = false;
    }
  }, [navigate, gateForPaths]);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (session?.user) check(session.user.id);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === "SIGNED_OUT" || !session) {
          hasRedirectedRef.current = false;
          checkedUserIdsRef.current.clear();
          return;
        }
        if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
          if (session?.user) check(session.user.id);
        }
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [check]);
}
